import { useState } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { FolderKanban, Plus, Loader2 } from 'lucide-react';
import { usePipelines, usePipelineStatuses, useSeedDefaultPipeline } from '@/hooks/usePipelines';
import { useProjects } from '@/hooks/useProjects';
import { ProjectKanbanBoard } from '@/components/projects/ProjectKanbanBoard';
import { CreateProjectDialog } from '@/components/projects/CreateProjectDialog';

export default function Projects() {
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  const [activePipelineId, setActivePipelineId] = useState<string | null>(null);
  const seedPipeline = useSeedDefaultPipeline();

  const selectedPipelineId = activePipelineId ?? pipelines?.[0]?.id ?? null;
  const selectedPipeline = pipelines?.find(p => p.id === selectedPipelineId) ?? null;

  const { data: statuses, isLoading: statusesLoading } = usePipelineStatuses(selectedPipelineId);
  const { data: projects, isLoading: projectsLoading } = useProjects(selectedPipelineId ?? undefined);

  const [createOpen, setCreateOpen] = useState(false);
  const [createStatusId, setCreateStatusId] = useState<string | null>(null);

  const isLoading = pipelinesLoading || statusesLoading || projectsLoading;

  // Empty state — no pipelines, or the only pipeline has no stages (a seed that
  // failed half-way leaves exactly that behind; the seed reuses it).
  const noPipelines = !pipelinesLoading && (!pipelines || pipelines.length === 0);
  const noStages =
    !pipelinesLoading && !statusesLoading && !!selectedPipelineId &&
    (pipelines?.length ?? 0) === 1 && (statuses?.length ?? 0) === 0;
  if (noPipelines || noStages) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <FolderKanban className="h-16 w-16 text-muted-foreground/40" />
          <h2 className="text-2xl font-bold">{noStages ? 'Pipeline Has No Stages' : 'No Pipeline Set Up'}</h2>
          <p className="text-muted-foreground text-center max-w-sm">
            {noStages
              ? 'Your pipeline has no stages yet, so projects have nowhere to go. Add the default stages to get started.'
              : 'Before tracking projects, you need at least one pipeline with stages. Create a default one to get started.'}
          </p>
          <Button onClick={() => seedPipeline.mutate()} disabled={seedPipeline.isPending}>
            {seedPipeline.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
            {noStages ? 'Add Default Stages' : 'Create Default Pipeline'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Or configure pipelines in <strong>Settings → Projects</strong>
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold">Projects</h1>
            <p className="text-muted-foreground">Track installation projects through your pipeline</p>
          </div>
          <Button
            onClick={() => { setCreateStatusId(statuses?.[0]?.id ?? null); setCreateOpen(true); }}
            disabled={!selectedPipeline}
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Pipeline tabs */}
        {pipelinesLoading ? (
          <div className="flex gap-2">
            <Skeleton className="h-7 w-28 rounded-full" />
            <Skeleton className="h-7 w-28 rounded-full" />
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {pipelines?.map(p => (
              <Badge
                key={p.id}
                variant={selectedPipelineId === p.id ? 'default' : 'outline'}
                className="cursor-pointer px-3 py-1 text-sm"
                onClick={() => setActivePipelineId(p.id)}
              >
                {p.name}
              </Badge>
            ))}
          </div>
        )}

        {/* Kanban board */}
        {isLoading ? (
          <div className="flex gap-4 overflow-x-auto pb-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="w-72 flex-shrink-0 space-y-3">
                <Skeleton className="h-6 w-32" />
                <Skeleton className="h-24 w-full rounded-lg" />
                <Skeleton className="h-20 w-full rounded-lg" />
              </div>
            ))}
          </div>
        ) : (
          <ProjectKanbanBoard
            statuses={statuses ?? []}
            projects={projects ?? []}
            onAddProject={statusId => {
              setCreateStatusId(statusId);
              setCreateOpen(true);
            }}
          />
        )}
      </div>

      {/* Create project dialog */}
      {createOpen && selectedPipeline && (
        <CreateProjectDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          pipeline={selectedPipeline}
          defaultStatusId={createStatusId}
        />
      )}
    </Layout>
  );
}
